import React, { Suspense } from 'react'
import { useLocation } from 'react-router'
import { useMount } from 'react-use'

import Banners from '@/components/banners/Banners'
import AppHeader from '@/components/layouts/DashboardLayout/components/DashboardSidebar/components/AppHeader'
import DashboardSidebar from '@/components/layouts/DashboardLayout/components/DashboardSidebar/DashboardSidebar'
import DashboardSidebarProvider from '@/components/layouts/DashboardLayout/components/DashboardSidebar/DashboardSidebarProvider'
import Footer from '@/components/layouts/DashboardLayout/components/Footer'
import CompanyDashboardGlobalModals from '@/components/misc/CompanyDashboardGlobalModals'
import PageContent from '@/components/misc/PageContent'
import Payments from '@/components/misc/Payments/Payments'
import SentryErrorBoundary from '@/components/misc/SentryErrorBoundary'
import IcaewModals from '@/components/modals/IcaewModals/IcaewModals'
import SelectServicesModal from '@/components/modals/SelectServicesModal'
import { SidebarInset } from '@/components/ui/sidebar'
import LoadingWidget from '@/components/widgets/LoadingWidget'
import useAppDispatch from '@/hooks/useAppDispatch'
import { useAppStateQuery } from '@/hooks/useAppStateQuery'
import { useCompanyConsentQuery } from '@/hooks/useCompanyConsentQuery'
import { useCompanyDashboardSidebarLinks } from '@/hooks/useCompanyDashboardSidebarLinks'
import useCurrentCompanyOrFail from '@/hooks/useCurrentCompanyOrFail'
import { useCurrentUserOrFail } from '@/hooks/useCurrentUserOrFail'
import { useSessionMetadataOrFail } from '@/hooks/useSessionMetadataOrFail'
import { COMPANY_DASHBOARD_LAYOUT_MAIN_CONTENT } from '@/pages/intellifloIntegration/constants'
import { LAYOUTS, setLayout } from '@/redux/slices/layout'

interface Props {
  children: React.ReactNode
  padded?: boolean
  fullscreen?: boolean
}

const CompanyDashboardLayout: React.FC<Props> = ({
  children,
  padded = true,
  fullscreen = false,
}) => {
  const location = useLocation()
  const dispatch = useAppDispatch()
  const sessionMetadata = useSessionMetadataOrFail()
  const currentUser = useCurrentUserOrFail()
  const currentCompany = useCurrentCompanyOrFail()
  const sidebarLinks = useCompanyDashboardSidebarLinks()
  const shouldShowServicesModal =
    currentUser.registeredViaReferralInvite &&
    currentCompany.services.length < 2

  useCompanyConsentQuery()

  useMount(() => {
    dispatch(setLayout(LAYOUTS.companyDashboard))
  })

  const appStateQuery = useAppStateQuery()

  if (appStateQuery.isLoading) {
    return <LoadingWidget />
  }

  const shouldShowIcaewModals =
    sessionMetadata.icaewModals &&
    Object.values(sessionMetadata.icaewModals).some(
      (shouldShow) => !!shouldShow,
    ) &&
    !shouldShowServicesModal

  return (
    <>
      <Payments />
      <Banners />
      {shouldShowServicesModal && <SelectServicesModal />}

      {shouldShowIcaewModals && <IcaewModals />}

      <DashboardSidebarProvider>
        {!fullscreen && <DashboardSidebar links={sidebarLinks} />}

        <SidebarInset className="min-w-0">
          <div
            className="flex flex-1 flex-row"
            id={COMPANY_DASHBOARD_LAYOUT_MAIN_CONTENT}
          >
            <div className="max-w-full flex-1 overflow-x-auto">
              {!fullscreen && <AppHeader />}

              <PageContent padded={padded}>
                <SentryErrorBoundary key={location.pathname}>
                  <Suspense>{children}</Suspense>
                </SentryErrorBoundary>
              </PageContent>
            </div>
          </div>

          {!fullscreen && <Footer />}
        </SidebarInset>

        <CompanyDashboardGlobalModals />
      </DashboardSidebarProvider>
    </>
  )
}

export default CompanyDashboardLayout
